<h2 lvrPadding [padHorizontal]="32" [padVertical]="0">{{ 'assignment.edit.title' | transloco }}</h2>

<form [formGroup]="form" autocomplete="on" lvrAutoSave="fds" [entityId]="draftId()" [autoLoad]="false">
	<lvr-section>
		<lvr-section-form>
			<!-- Datum der Beauftragung -->
			@let dateCtrl = form.get('dateOfCommission');
			<lvr-field [label]="'assignment.create_fds.dateOfCommission' | transloco | appendAsterisk">
				<mat-form-field lvrInput [attr.aria-label]="'assignment.create_fds.dateOfCommission' | transloco">
					<input
						matInput
						[matDatepicker]="picker"
						[min]="minDateOfCommission()"
						[max]="maxDateOfCommission()"
						formControlName="dateOfCommission"
						placeholder="01.07.2025"
						[attr.aria-label]="'assignment.create_fds.dateOfCommission' | transloco"
						[errorStateMatcher]="matcher"
					/>
					@if (hasError(dateCtrl, 'minDateExceeded')) {
						<mat-error>{{
							'assignment.create_fds.errors.min_date'
								| transloco: { min: (minDateOfCommission() | date: dateDisplayFormat) }
						}}</mat-error>
					} @else if (hasError(dateCtrl, 'maxDateExceeded')) {
						<mat-error>{{ 'assignment.create_fds.errors.max_date' | transloco }}</mat-error>
					} @else {
						<mat-hint>{{ dateFormatHint }}</mat-hint>
					}
					<mat-datepicker-toggle
						matIconSuffix
						[for]="picker"
						[attr.aria-label]="'assignment.create_fds.dateOfCommission' | transloco"
					></mat-datepicker-toggle>
					<mat-datepicker #picker></mat-datepicker>
				</mat-form-field>
			</lvr-field>

			<!-- Auftraggeber -->
			@let sponsorCtrl = form.get('sponsorId');
			<lvr-field [label]="'base.field.sponsor' | transloco | appendAsterisk">
				<mat-form-field
					lvrInput
					[lvrError]="hasError(sponsorCtrl, 'required')"
					[attr.aria-label]="'base.field.sponsor' | transloco"
				>
					<mat-select
						formControlName="sponsorId"
						[placeholder]="'base.field.select_please' | transloco"
						aria-required="true"
						[errorStateMatcher]="matcher"
					>
						@for (option of sponsors(); track option.id) {
							<mat-option [value]="option.id">
								{{ option.name }}
							</mat-option>
						}
					</mat-select>
					@if (sponsors().length === 0) {
						<mat-hint>{{ 'base.form.hint.no_initiators' | transloco }}</mat-hint>
					} @else if (hasError(sponsorCtrl, 'required')) {
						<mat-error>{{ 'base.form.error.sponsor_required' | transloco }}</mat-error>
					}
				</mat-form-field>
			</lvr-field>

			<!-- Grundlage -->
			@let legalFoundationCtrl = form.get('legalFoundationId');
			<lvr-field [label]="'base.field.legal_foundation' | transloco | appendAsterisk">
				<mat-form-field
					lvrInput
					[lvrError]="hasError(legalFoundationCtrl, 'required')"
					[attr.aria-label]="'base.field.legal_foundation' | transloco"
				>
					<mat-select
						formControlName="legalFoundationId"
						[placeholder]="'base.field.select_please' | transloco"
						aria-required="true"
						[errorStateMatcher]="matcher"
					>
						@for (option of availableLegalFoundation(); track option.id) {
							<mat-option [value]="option.id">
								{{ option.description }}
							</mat-option>
						}
					</mat-select>
					@if (!sponsorCtrl!.value) {
						<mat-hint>{{ 'base.form.hint.no_sponsor_selected' | transloco }}</mat-hint>
					} @else if (availableLegalFoundation().length === 0) {
						<mat-hint>{{ 'base.form.hint.no_legal_foundation' | transloco }}</mat-hint>
					} @else if (hasError(legalFoundationCtrl, 'required')) {
						<mat-error>{{ 'base.form.error.legal_foundation_required' | transloco }}</mat-error>
					} @else {
						<mat-hint>{{ 'base.field.legal_foundation_hint' | transloco }}</mat-hint>
					}
				</mat-form-field>
			</lvr-field>

			<!-- Betreuungsrelevante Behinderungsart -->
			<lvr-field [label]="'base.field.type_of_disability_relevant_to_care' | transloco" style="margin-bottom: 1rem">
				<mat-form-field lvrInput [attr.aria-label]="'base.field.select_please' | transloco">
					<mat-select
						#sel="matSelect"
						formControlName="careRelatedHandicapId"
						[placeholder]="'base.field.type_of_disability_relevant_to_care' | transloco"
						aria-required="true"
					>
						<mat-select-trigger>
							{{ getDisabilityKindName(sel.value) }}
							<span class="text-sm differentiation">{{ getDisabilityKindDifferentiation(sel.value) }}</span>
						</mat-select-trigger>

						@for (option of disabilityKinds(); track option.id) {
							<mat-option class="option" [value]="option.id">
								<span class="remark">{{ option.disabilityType?.remark ?? '' }}</span>
								<span class="text-sm">{{ option.categoryDiagnosis?.description ?? '-' }}</span>
							</mat-option>
						}
					</mat-select>
					@if (disabilityKinds().length === 0) {
						<mat-hint>
							{{ 'assignment.edit.no_disability_kinds_prefix' | transloco }}
							<a
								[routerLink]="['/desk/client', client()?.id, 'disabilities', 'type', 'add']"
								[queryParams]="queryParams"
								(click)="saveDraftBeforeNavigation()"
							>
								{{ 'assignment.edit.no_disability_kinds_link' | transloco }}
							</a>
							{{ 'assignment.edit.no_disability_kinds_suffix' | transloco }}
						</mat-hint>
					}
				</mat-form-field>
			</lvr-field>

			<!-- Stellungnahme -->
			@if (showInitialStatementSelect() && form.get('initialStatement')?.enabled) {
				@let initialStatementCtrl = form.get('initialStatement');
				<lvr-field [label]="'assignment.create_fds.statement_type' | transloco | appendAsterisk">
					<mat-form-field
						lvrInput
						[lvrError]="hasError(initialStatementCtrl, 'required')"
						[attr.aria-label]="'assignment.create_fds.statement_type' | transloco"
					>
						<mat-select
							formControlName="initialStatement"
							[placeholder]="'base.field.select_please' | transloco"
							aria-required="true"
							[errorStateMatcher]="matcher"
						>
							<mat-option [value]="true">{{ 'assignment.create_fds.statement_type_initial' | transloco }}</mat-option>
							<mat-option [value]="false">{{
								'assignment.create_fds.statement_type_follow_up' | transloco
							}}</mat-option>
						</mat-select>
						@if (hasError(initialStatementCtrl, 'required')) {
							<mat-error>{{ 'base.form.error.required' | transloco }}</mat-error>
						}
					</mat-form-field>
				</lvr-field>
				<lvr-field></lvr-field>
			}

			<!-- Merkblatt Sozialdatenschutz -->
			<lvr-data-protection
				formControlName="clarification"
				[showAsterisk]="isSubmit()"
				[locked]="isClarificationFinal()"
				[label]="'base.field.social_data_protection_info'"
				[minDateInput]="clarificationMinDate()"
				[maxDateInput]="clarificationMaxDate()"
			></lvr-data-protection>

			@if (isSubmitView()) {
				<lvr-field [label]="endOfCareLabelKey() | transloco">
					<mat-form-field lvrInput [attr.aria-label]="endOfCareLabelKey() | transloco">
						<mat-select
							formControlName="endOfCareId"
							[placeholder]="'base.field.select_please' | transloco"
							[attr.aria-label]="endOfCareLabelKey() | transloco"
						>
							@for (eoc of endOfCares(); track eoc.id) {
								<mat-option [value]="eoc.id">{{ eoc.designation }}</mat-option>
							}
						</mat-select>
					</mat-form-field>
				</lvr-field>
			}
			@let documentsCtrl = form.get('documents');
			<lvr-field
				[label]="
					documentsRequired()
						? ('base.field.documents' | transloco | appendAsterisk)
						: ('base.field.documents' | transloco)
				"
			>
				<lvr-document-upload
					[maxFileCount]="maxFileCount"
					[hasError]="hasFileError(documentsCtrl, 'requiredFile', 'maxFiles', 'maxFileSize', 'allowedFileTypes')"
					[multiple]="true"
					formControlName="documents"
					[showIcon]="true"
					[ownerId]="client()?.uuid ?? clientId()!"
					[maxFileSize]="maxFileSize"
					[subjectType]="subjectType"
					[disabled]="!isEditable() || !canManageAssignmentDocuments()"
				></lvr-document-upload>
			</lvr-field>
		</lvr-section-form>
	</lvr-section>

	<div class="assignment-edit-actions-row">
		<p lvrPadding [padHorizontal]="0" [padVertical]="24">{{ 'base.form.required' | transloco }}</p>

		<lvr-default-actions
			[disableSave]="form.invalid || !canEditAssignmentsPermission()"
			(save)="save()"
			(cancelSave)="cancel()"
			[showLoading]="isLoading()"
		>
			<lvr-restore-actions entityType="fds" [entityId]="draftId()"></lvr-restore-actions>
		</lvr-default-actions>
	</div>
</form>
